import React, {
  useEffect, useRef, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import RootContainer from 'components/custom/RootContainer';
import CustomPageIframe from 'components/custom/CustomPageIframe';
import CustomPageFooter from 'components/custom/CustomPageFooter';
import CustomHelmet from 'components/CustomHelmet';
import { useTranslation } from 'react-i18next';
import { selectLocale } from 'models/localization';
import { useLocation } from 'react-router-dom';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { selectLocalizedEventName } from 'models/virtual_event';

const CustomPageContainer = () => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const iframeRef: React.RefObject<HTMLIFrameElement> = useRef();
  const { pathname } = useLocation();
  const [currentPage, setCurrentPage] = useState(null);
  const {
    customPage,
  } = useSelector((state: any) => state.eventConfigs);
  const eventName = useSelector(selectLocalizedEventName);

  useEffect(() => {
    const allCustomPages = customPage.flatMap((curr) => curr.pages);
    const tempCurrentPage = allCustomPages.find((page) => {
      const pageLocalePathPair = Object.entries(page.path);
      const currentPair = pageLocalePathPair.find((path) => path[1] === pathname);
      if (currentPair) return true;
      return false;
    });
    setCurrentPage(tempCurrentPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const pageKind = currentPage?.kind;
  const pageSource = useMemo(() => findAvailableLocale((currentPage || {}).src || {}, locale), [currentPage, locale]);
  const customHtml = useMemo(() => findAvailableLocale((currentPage || {}).customHtml || {}, locale), [currentPage, locale]);
  const pageTitle = useMemo(() => findAvailableLocale((currentPage || {}).pageTitle || {}, locale), [currentPage, locale]) || t('page.custom', 'Custom Page');
  const disableWebTitlePattern = !!currentPage?.disableWebTitlePattern;
  const webTitle = useMemo(() => {
    if (disableWebTitlePattern) return pageTitle;
    return pageTitle ? `${pageTitle} | ${eventName}` : eventName;
  }, [eventName, pageTitle, disableWebTitlePattern]);

  useEffect(() => {
    // Return when NOT using HTML
    if (pageKind === 'WEBSITE' && pageSource) return;
    const iframeElement: HTMLIFrameElement = iframeRef.current;
    if (customHtml && iframeElement) {
      iframeElement.src = 'about:blank';
      iframeElement.contentWindow.document.open();
      iframeElement.contentWindow.document.write(customHtml);
      iframeElement.contentWindow.document.close();
    }
  }, [pageSource, customHtml, pageKind]);

  if (!pageSource && !customHtml) {
    return null;
  }

  return (
    <>
      <CustomHelmet>
        <meta charSet="utf-8" />
        <title>{webTitle}</title>
        <meta name="description" content={`${pageTitle} of ${eventName}.`} />
      </CustomHelmet>
      <RootContainer>
        <CustomPageIframe
          title={pageTitle}
          key={`${pageKind}-${pageTitle}`}
          ref={iframeRef}
          width="100%"
          height="100%"
          src={pageKind === 'WEBSITE' && pageSource}
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
        {!currentPage?.removeFooter && <CustomPageFooter pathname={pathname} />}
      </RootContainer>
    </>
  );
};

export default CustomPageContainer;
